<template>
  <div class="col-sm-12 editor-form">
    <!-- Attachments-->
    <h4 class="form-header">
      {{ $t('contracts.attachments') }}
    </h4>
    <span class="tip-light">{{ $t('contracts.attachment_hint') }}</span>
    <div v-if="contract.attachments && contract.attachments.length" class="mt-2">
      <div
        v-for="(item, index) in contract.attachments"
        :key="item.id"
        class="row border-bottom pb-1 mx-1"
        style="font-size: .9em"
      >
        <div
          class="col-10 pointer link pl-0 pt-1 ml-0"
          :class="{ overline: isRemoved(index) }"
          @click="downloadAttachment(item)"
        >
          {{ item.file_original_name }}
        </div>
        <div class="col-2">
          <button
            class="btn btn-danger btn-sm ms-3"
            :disabled="isRemoved(index)"
            @click.stop="removeExistingAttachment(index)"
          >
            <font-awesome-icon icon="trash" />
          </button>
        </div>
      </div>
    </div>
    <!-- Attachment upload-->
    <div>
        <div class="col-12">
            <!-- Show selected attachments -->
            <div
                v-for="(file, index) in attachments"
                :key="index"
                class="row border-bottom pb-1 mx-1"
                style="font-size: .9em"
            >
                <div class="col-10 pointer link pl-0 pt-1 ml-0">
                    {{ file?.name || file }}
                </div>
                <div class="col-2">
                    <button
                        class="btn btn-danger btn-sm ms-3"
                        @click.stop="removeUnsentAttachment(index)"
                    >
                        <font-awesome-icon icon="trash" />
                    </button>
                </div>
            </div>

            <!-- Hidden file input -->
            <input
                ref="fileInput"
                type="file"
                :accept="getValidAttachmentTypes()"
                class="visually-hidden"
                @change="handleFileChange"
            />

        </div>
    </div>
    <div class="col-sm-12 d-flex justify-content-end my-3">
        <!-- Custom "Add file" button -->
      <button class="btn btn-outline-primary form-button" type="button" @click="triggerFileInput">
            {{ $t('import_file_wizard.import_file') }}
      </button>
      <button class="btn btn-secondary form-button" @click.stop="reset">
        {{ $t('common.cancel') }}
      </button>
      <button class="btn btn-success form-button" :disabled="loading" @click.stop="updateAttachments">
        <span v-if="contract.id > 0">
          {{ $t('common.save') }}
        </span>
      </button>
    </div>
    <div
      v-if="loading"
      id="loader"
      class="spinner"
    />
  </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {workManagementHelper} from '../mixins/WorkManagementMixin'
import {attachmentHelper} from "@/components/mixins/AttachmentMixin";
import {inject} from "vue";

export default {
    name: 'ContractDocuments',
    mixins: [workManagementHelper, restApi, attachmentHelper],
    props: {
        modelValue: {
            type: Object,
            default: null
        }
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: false,
            contract: null,
            attachments: [],
            removedAttachments: []
        }
    },
    watch: {
      modelValue: function() {
          this.contract = this.modelValue
      },
    },
    created() {
        this.contract = this.modelValue
    },
    methods: {

        triggerFileInput() {
            this.$refs.fileInput?.click();
        },
        handleFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                this.attachments.push(file);
            }
            // Clear input value so the same file can be selected again if needed
            event.target.value = '';
        },

        isRemoved(index) {
            let item = this.contract.attachments[index]
            let removed = this.removedAttachments.find(remo => remo.id === item.id)
            return removed !== undefined
        },

        removeExistingAttachment(index) {
            // Mark attachment as removed
            if (!this.isRemoved(index)) {
                this.removedAttachments.push(this.contract.attachments[index])
            }
        },

        removeUnsentAttachment(index) {
            this.attachments.splice(index, 1);
        },

        updateContract: function () {
            if (this.contract.id > 0) {
                this.loading = true
                this.restFetchParams(this.contractUrl, {id: this.contract.id}, this.successUpdate, this.fail)
            }
        },

        successUpdate: function (response) {
            if (response.data && response.data.length > 0) {
                let contractData = response.data[0]
                if (!contractData.area_group) {
                    contractData.area_group = {id: null}
                }
                this.contract = contractData
            }
            this.loading = false
        },

        reset() {
            this.contract = this.modelValue
            this.removedAttachments = []
            this.attachments = []
        },

        updateAttachments() {
            // Remove if needed
            if (this.removedAttachments.length > 0) {
                this.loading = true
                this.restUpdate(this.contractAttachmentRemoveUrl + "/" + this.contract.id, {attachments: this.removedAttachments},
                    () => {
                        this.removedAttachments = []
                        if (this.attachments.length > 0) {
                            this.uploadAttachment()
                        } else {
                            this.updateContract()
                        }
                    }
                    , this.fail
                    )
            } else {
                // Upload new
                this.uploadAttachment()
            }
        },

        uploadAttachment: function () {
            if (this.attachments.length > 0) {
                let attachments = this.getValidAttachments(this.attachments)
                if (attachments.length > 0) {
                    this.loading = true
                    let formData = new FormData()
                    attachments.forEach(file => {
                        if (file !== undefined && file !== null) {
                            formData.append('attachments[]', file)
                        }
                    })
                    this.restPostWithFile(this.contractAttachmentUrl + this.contract.id, {}, formData,
                        () => {
                            this.attachments = []
                            this.updateContract()
                        }, this.fail
                    )
                }
            } else {
                this.loading = false
            }
        },

        fail: function () {
            this.showAlert(this.$t('common.alert_update_failed'))
            this.loading = false
        },
        downloadAttachment: function (item) {
            this.loading = true
            this.restFetch(this.contractAttachmentUrl + this.contract.id + '/' + item.id,
                response => {
                    this.saveFile(item.file_original_name, response.data)
                    this.loading = false
                }, () => {
                    this.loading = false
                })
        }
    }
}
</script>
